import { useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminPage from '../components/AdminPage';
import Button from '../components/Button';
import TextInput from '../components/input/TextInput';
import TextAreaInput from '../components/input/TextAreaInput';
import List from '../components/List';
import Modal from '../components/Modal';
import { useCreateQuizMutation } from './mutations/createQuizMutation';
import { useGetClientQuery } from './queries/getClientQuery';
import styles from './AdminClientView.module.css';
import { completitionEmailOptions } from '../common';
import { format } from 'date-fns';

type Params = {
  readonly id: string;
};
const defaultIntroduction =
    'Welcome to the Culturesphere™ Culture Assessment – Individual Survey\n' +
    '\n' +
    'Culturesphere™ is an insights tool designed to uncover the cultural influences and expectations active inside your team.\n' +
    '\n' +
    'This survey has two parts. It takes approximately 10 minutes to complete. We encourage you to take as much time as you need to accurately complete each section.\n';

const defaultIntroductionLeader =
    'Welcome to the Culturesphere™ Culture Assessment – Leader Survey\n' +
    '\n' +
    'Culturesphere™ is an insights tool designed to uncover the cultural influences and expectations active inside your team.\n' +
    '\n' +
    'This survey takes approximately 5 minutes to complete. We encourage you to take as much time as you need to accurately complete each section.\n';

export function AdminClientView() {
  const [newSurveyTitle, setNewSurveyTitle] = useState('');
  const [newSurveyIntroduction, setNewSurveyIntroduction] =
      useState(defaultIntroduction);
  const [newSurveyTeamName, setNewSurveyTeamName] = useState('');
  const [newSurveyIntroductionLeader, setNewSurveyIntroductionLeader] =
      useState(defaultIntroductionLeader);
  const [sendCompletionEmailMode, setsendCompletionEmailMode] =
      useState('on_demand');

  const [newSurveyTemplateId, setNewSurveyTemplateId] = useState(
      // Todo: Hard-coded for now, but will want to make this dynamic eventually.
      // Doesn't add much immediate value though, so leaving it for now.
      '678c72be-32a3-47cd-970b-69c9c4ca05b6',
  );
  const [showCreateSurveyModal, setShowCreateSurveyModal] = useState(false);

  const { id } = useParams<Params>();
  const { data, refetch } = useGetClientQuery({ variables: { id: id! } });

  const [createQuiz, { loading: createQuizLoading }] = useCreateQuizMutation({
    variables: {
      input: {
        title: newSurveyTitle,
        introduction: newSurveyIntroduction,
        introductionLeader: newSurveyIntroductionLeader,
        teamName: newSurveyTeamName,
        client: id!,
        template: newSurveyTemplateId,
        sendCompletionEmailMode: sendCompletionEmailMode,
      },
    },
    onCompleted: (data) => {
      setNewSurveyTitle('');
      setNewSurveyIntroduction('');
      setShowCreateSurveyModal(false);
      setNewSurveyIntroductionLeader('');
      setNewSurveyTeamName('');
      setsendCompletionEmailMode('on_demand');

      return refetch();
    },
  });

  if (!data) {
    return null;
  }

  const openSurveys = data.quizzes.filter((quiz) => !quiz.closed);
  const closedSurveys = data.quizzes.filter((quiz) => quiz.closed);

  return (
      <AdminPage
          title={`Clients - ${data.client.name}`}
          // actions={
          //   <Button onClick={() => setShowCreateSurveyModal(true)}>
          //     Create Survey
          //   </Button>
          // }
      >
        <h2 className="text-lg font-bold mt-4 mb-2">Open Surveys</h2>
        <List
            items={openSurveys}
            render={(quiz) => (
                <div className="flex items-center justify-between">
                  <div>
                    {quiz.title}{' '}
                    {quiz.closed && (
                        <span className="text-red-500 text-sm">(Closed)</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                    {format(new Date(quiz.created), 'dd MMM yyyy')}
                  </span>
                </div>
            )}
            link={(quiz) => `/admin/surveys/${quiz.id}`}
        />

        <h2 className="text-lg font-bold mt-6 mb-2">Closed Surveys</h2>
        <List
            items={closedSurveys}
            render={(quiz) => (
                <div className="flex items-center justify-between">
                  <div>
                    {quiz.title}{' '}
                    {quiz.closed && (
                        <span className="text-red-500 text-sm">(Closed)</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                    {format(new Date(quiz.created), 'dd MMM yyyy')}
                  </span>
                </div>
            )}
            link={(quiz) => `/admin/surveys/${quiz.id}`}
        />

        {showCreateSurveyModal && (
            <Modal
                heading="Create New Survey"
                onClose={() => setShowCreateSurveyModal(false)}
            >
              <div className="space-y-2">
                <TextInput
                    placeholder="Title"
                    value={newSurveyTitle}
                    onChange={setNewSurveyTitle}
                />
                <TextInput
                    placeholder="Team Name"
                    value={newSurveyTeamName}
                    onChange={setNewSurveyTeamName}
                />
                <div>
                  <label>Introduction for individuals</label>
                  <TextAreaInput
                      placeholder="Introduction for individuals"
                      value={newSurveyIntroduction || defaultIntroduction}
                      onChange={setNewSurveyIntroduction}
                  />
                </div>

                <div>
                  <label>Introduction for leaders</label>
                  <TextAreaInput
                      placeholder="Introduction for leaders"
                      value={newSurveyIntroductionLeader || defaultIntroduction}
                      onChange={setNewSurveyIntroductionLeader}
                  />
                </div>
                <div>
                  <label>When the PDF reports for individuals are sent?</label>
                  {completitionEmailOptions.map((option) => (
                      <div key={option.value} className={styles.radioOption}>
                        <input
                            type="radio"
                            id={option.value}
                            name="completition_email"
                            value={option.value}
                            checked={sendCompletionEmailMode === option.value}
                            onChange={() => setsendCompletionEmailMode(option.value)}
                        />
                        <label htmlFor={option.value}>{option.label}</label>
                      </div>
                  ))}
                </div>

                <Button onClick={createQuiz} loading={createQuizLoading}>
                  Create
                </Button>
              </div>
            </Modal>
        )}
      </AdminPage>
  );
}